import { useState } from "react";
import CourseCard from "../components/CourseCard";
import { useTranslation } from "react-i18next";
import AcademyMeetups from "../components/AcademyMeetUps";

type Lang = "ka" | "en" | "ru";

export type CourseLang = {
  t: string;
  d: string;
  l: string[];
};

export type Course = {
  id: number;
  flag: string;
  price: string;
  t: string;
  d: string;
  l: string[];
};

export default function Academy() {
  const { t, i18n } = useTranslation();

  const [selected, setSelected] = useState<Course | null>(null);
  const [selectedMeetup, setSelectedMeetup] = useState<any | null>(null);

  const [form, setForm] = useState({ name: "", email: "", phone: "" });
  const [success, setSuccess] = useState(false);

  const [meetupForm, setMeetupForm] = useState({ name: "", email: "", phone: "" });
  const [meetupSuccess, setMeetupSuccess] = useState(false);

  const lang: Lang = (["ka", "en", "ru"].includes(i18n.language) ? i18n.language : "en") as Lang;

  const courses = (t("academy.courses", { returnObjects: true }) as Course[]) || [];

  const handleSubmit = () => {
    setSuccess(true);
    setForm({ name: "", email: "", phone: "" });
    setTimeout(() => setSuccess(false), 3000);
  };

  const handleMeetupSubmit = () => {
    setMeetupSuccess(true);
    setMeetupForm({ name: "", email: "", phone: "" });
    setTimeout(() => setMeetupSuccess(false), 3000);
  };

  return (
    <section className="min-h-screen bg-[#fbfcfd] px-4 sm:px-8 lg:px-12 py-4 sm:py-6 lg:py-8 space-y-8 sm:space-y-10 lg:space-y-12">

      {/* HERO */}
      <section className="pt-2 sm:pt-4 lg:pt-6 pb-4 sm:pb-8 text-center space-y-4 sm:space-y-5 max-w-[1450px] mx-auto">
        <h1 className="text-3xl sm:text-5xl lg:text-[3.5rem] font-black uppercase tracking-tighter text-slate-900 leading-[0.95] break-words">
          GeoAlpha{" "}
          <span className="text-blue-600 italic">
            {t("academy.title")}
          </span>
        </h1>
        <p className="text-sm sm:text-base lg:text-lg text-slate-400 font-light italic leading-relaxed max-w-2xl mx-auto">
          {t("academy.subtitle")}
        </p>
      </section>

      {/* GRID */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-5 lg:gap-6 max-w-[1450px] mx-auto">
        {courses.map((course) => (
          <CourseCard
            key={course.id}
            course={course}
            onSelect={setSelected}
          />
        ))}
      </div>

      {/* COURSE MODAL */}
      {selected && (
        <div style={{ position: "absolute", height: "100vh" }}>
          <div
            className="
              fixed inset-0
              z-[9999]
              bg-black/60
              backdrop-blur-[12px]
              flex items-center justify-center
              p-3 sm:p-6 lg:p-10
            "
            onClick={() => setSelected(null)}
          >
            <div
              className="
                relative bg-white
                rounded-2xl sm:rounded-[36px] lg:rounded-[44px]
                w-full max-w-xs sm:max-w-2xl lg:max-w-5xl
                overflow-hidden
                flex flex-col md:flex-row
                max-h-[92vh] sm:max-h-[88vh]
                shadow-2xl
              "
              onClick={(e) => e.stopPropagation()}
            >
              {success && (
                <div className="absolute top-3 sm:top-4 left-1/2 -translate-x-1/2 z-[10000] bg-blue-600 text-white px-4 sm:px-6 py-2 sm:py-3 rounded-full shadow-xl font-black text-xs sm:text-sm whitespace-nowrap">
                  {t("contact.success")}
                </div>
              )}

              {/* LEFT */}
              <div className="md:w-1/2 p-5 sm:p-7 lg:p-10 bg-slate-50 overflow-y-auto">
                <div className="flex justify-between items-start mb-4 sm:mb-6 lg:mb-8">
                  <div className="w-9 h-9 sm:w-11 sm:h-11 lg:w-12 lg:h-12 bg-blue-50 text-blue-600 rounded-xl sm:rounded-2xl flex items-center justify-center font-black text-base sm:text-lg">
                    ⚡
                  </div>
                  <img
                    src={`https://flagcdn.com/w80/${selected.flag}.png`}
                    className="w-8 sm:w-9 lg:w-10 h-5 sm:h-6 lg:h-7 rounded-md"
                    alt="flag"
                  />
                </div>

                <h3 className="text-lg sm:text-2xl lg:text-3xl font-black uppercase tracking-tight mb-3 sm:mb-4 break-words leading-tight">
                  {selected.t}
                </h3>

                <div className="flex items-center gap-2 mb-4 sm:mb-5 lg:mb-6">
                  <span className="text-[9px] sm:text-[10px] font-black text-blue-600 uppercase tracking-widest">
                    {t("academy.price")}:
                  </span>
                  <span className="text-base sm:text-lg lg:text-xl font-black">
                    {selected.price}
                  </span>
                </div>

                <p className="text-xs sm:text-sm lg:text-base text-slate-500 italic mb-5 sm:mb-6 lg:mb-8 leading-relaxed">
                  {selected.d}
                </p>

                <ul className="space-y-2 sm:space-y-3">
                  {selected.l?.map((item: string) => (
                    <li
                      key={item}
                      className="flex items-start gap-2 sm:gap-3 text-xs sm:text-sm lg:text-base font-medium text-slate-700"
                    >
                      <span className="mt-1.5 sm:mt-2 w-1.5 h-1.5 bg-blue-600 rounded-full min-w-[6px]" />
                      {item}
                    </li>
                  ))}
                </ul>
              </div>

              {/* RIGHT */}
              <div className="md:w-1/2 p-5 sm:p-7 lg:p-10 flex flex-col justify-center space-y-3 sm:space-y-4 lg:space-y-5">
                <h4 className="text-lg sm:text-xl lg:text-2xl font-black uppercase">
                  {t("academy.registration")}
                </h4>

                <input
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  className="px-4 sm:px-5 py-3 sm:py-3.5 lg:py-4 bg-slate-50 border rounded-xl sm:rounded-2xl font-bold text-sm"
                  placeholder={t("academy.form.name") as string}
                />
                <input
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  className="px-4 sm:px-5 py-3 sm:py-3.5 lg:py-4 bg-slate-50 border rounded-xl sm:rounded-2xl font-bold text-sm"
                  placeholder={t("academy.form.email") as string}
                />
                <input
                  value={form.phone}
                  onChange={(e) => setForm({ ...form, phone: e.target.value })}
                  className="px-4 sm:px-5 py-3 sm:py-3.5 lg:py-4 bg-slate-50 border rounded-xl sm:rounded-2xl font-bold text-sm"
                  placeholder={t("academy.form.phone") as string}
                />

                <button
                  onClick={handleSubmit}
                  className="w-full py-3.5 sm:py-4 lg:py-5 bg-blue-600 text-white font-black uppercase text-[10px] sm:text-[11px] rounded-2xl sm:rounded-3xl hover:bg-slate-900 transition-all active:scale-95"
                >
                  {t("academy.button")}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <AcademyMeetups onSelect={setSelectedMeetup} />

      {/* MEETUP MODAL */}
      {selectedMeetup && (
        <div style={{ position: "absolute", height: "100vh" }}>
          <div
            className="
              fixed inset-0
              z-[9999]
              bg-black/60
              backdrop-blur-[12px]
              flex items-center justify-center
              p-3 sm:p-6 lg:p-10
            "
            onClick={() => setSelectedMeetup(null)}
          >
            <div
              className="
                relative bg-white
                rounded-2xl sm:rounded-[36px] lg:rounded-[44px]
                w-full max-w-xs sm:max-w-2xl lg:max-w-5xl
                overflow-hidden
                flex flex-col md:flex-row
                max-h-[92vh] sm:max-h-[88vh]
                shadow-2xl
              "
              onClick={(e) => e.stopPropagation()}
            >
              {meetupSuccess && (
                <div className="absolute top-3 sm:top-4 left-1/2 -translate-x-1/2 z-[10000] bg-blue-600 text-white px-4 sm:px-6 py-2 sm:py-3 rounded-full shadow-xl font-black text-xs sm:text-sm whitespace-nowrap">
                  {t("contact.success")}
                </div>
              )}

              {/* LEFT */}
              <div className="md:w-1/2 p-5 sm:p-7 lg:p-10 bg-slate-50 overflow-y-auto">
                <div className="w-9 h-9 sm:w-11 sm:h-11 lg:w-12 lg:h-12 bg-blue-50 text-blue-600 rounded-xl sm:rounded-2xl flex items-center justify-center font-black mb-4 sm:mb-5 lg:mb-6 text-base sm:text-lg">
                  ⚡
                </div>

                <h3 className="text-lg sm:text-2xl lg:text-3xl font-black uppercase tracking-tight mb-3 sm:mb-4 leading-tight">
                  {selectedMeetup.title}
                </h3>

                <p className="text-xs sm:text-sm lg:text-base text-slate-500 italic leading-relaxed mb-5 sm:mb-6 lg:mb-8">
                  {selectedMeetup.goal}
                </p>

                <div className="space-y-4 sm:space-y-5 lg:space-y-6">
                  <div>
                    <h4 className="text-[10px] sm:text-xs font-black uppercase tracking-widest mb-3 sm:mb-4">
                      {t("academyMeetups.build")}
                    </h4>
                    <ul className="space-y-2 sm:space-y-3">
                      {selectedMeetup.build.map((item: string) => (
                        <li key={item} className="flex items-start gap-2 sm:gap-3 text-xs sm:text-sm text-slate-700">
                          <span className="mt-1.5 sm:mt-2 w-1.5 h-1.5 bg-blue-600 rounded-full min-w-[6px]" />
                          {item}
                        </li>
                      ))}
                    </ul>
                  </div>

                  <div>
                    <h4 className="text-[10px] sm:text-xs font-black uppercase tracking-widest mb-3 sm:mb-4">
                      {t("academyMeetups.learn")}
                    </h4>
                    <ul className="space-y-2 sm:space-y-3">
                      {selectedMeetup.learn.map((item: string) => (
                        <li key={item} className="flex items-start gap-2 sm:gap-3 text-xs sm:text-sm text-slate-700">
                          <span className="mt-1.5 sm:mt-2 w-1.5 h-1.5 bg-slate-900 rounded-full min-w-[6px]" />
                          {item}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>

              {/* RIGHT */}
              <div className="md:w-1/2 p-5 sm:p-7 lg:p-10 flex flex-col justify-center space-y-3 sm:space-y-4 lg:space-y-5">
                <h4 className="text-lg sm:text-xl lg:text-2xl font-black uppercase">
                  {t("academy.registration")}
                </h4>

                <input
                  value={meetupForm.name}
                  onChange={(e) => setMeetupForm({ ...meetupForm, name: e.target.value })}
                  className="px-4 sm:px-5 py-3 sm:py-3.5 lg:py-4 bg-slate-50 border rounded-xl sm:rounded-2xl font-bold text-sm"
                  placeholder={t("academy.form.name") as string}
                />
                <input
                  value={meetupForm.email}
                  onChange={(e) => setMeetupForm({ ...meetupForm, email: e.target.value })}
                  className="px-4 sm:px-5 py-3 sm:py-3.5 lg:py-4 bg-slate-50 border rounded-xl sm:rounded-2xl font-bold text-sm"
                  placeholder={t("academy.form.email") as string}
                />
                <input
                  value={meetupForm.phone}
                  onChange={(e) => setMeetupForm({ ...meetupForm, phone: e.target.value })}
                  className="px-4 sm:px-5 py-3 sm:py-3.5 lg:py-4 bg-slate-50 border rounded-xl sm:rounded-2xl font-bold text-sm"
                  placeholder={t("academy.form.phone") as string}
                />

                <button
                  onClick={handleMeetupSubmit}
                  className="
                    w-full py-3.5 sm:py-4 lg:py-5
                    bg-blue-600 text-white
                    font-black uppercase text-[10px] sm:text-[11px]
                    rounded-2xl sm:rounded-3xl
                    hover:bg-slate-900
                    transition-all
                    active:scale-95
                  "
                >
                  {t("academy.button")}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

    </section>
  );
}